import { log } from '@reusables/logger'

/**
 * Sanitize data for logging - preserve essential debugging info while removing PII
 */
function sanitizeForLogging(data, type = 'general') {
	if (!data || typeof data !== 'object') return data

	const sanitized = {}

	if (type === 'params') {
		// For tool parameters - keep IDs, collections, filters but remove sensitive data
		if (data.collection) sanitized.collection = data.collection
		if (data.id) sanitized.id = data.id
		if (data.userId) sanitized.userId = data.userId
		if (data.docId) sanitized.docId = data.docId
		if (data.limit) sanitized.limit = data.limit
		if (data.orderBy) sanitized.orderBy = data.orderBy
		if (data.filters) {
			// Sanitize filters - keep structure but mask email values
			sanitized.filters = Array.isArray(data.filters)
				? data.filters.map((filter) => ({
						field: filter.field,
						operator: filter.operator,
						value: filter.field === 'email' ? '[EMAIL_MASKED]' : filter.value
					}))
				: data.filters
		}
		if (data.searchTerm && typeof data.searchTerm === 'string') {
			// Mask emails in search terms but keep other text
			sanitized.searchTerm = data.searchTerm.includes('@')
				? '[EMAIL_SEARCH_MASKED]'
				: data.searchTerm
		}
	} else if (type === 'context') {
		// For context - keep IDs and tenant info but remove sensitive data
		if (data.userId) sanitized.userId = data.userId
		if (data.tenantId) sanitized.tenantId = data.tenantId
		if (data.lastDocumentId) sanitized.lastDocumentId = data.lastDocumentId
		if (data.lastCollection) sanitized.lastCollection = data.lastCollection
		if (data.mode) sanitized.mode = data.mode
		// Don't log currentUser object as it may contain emails/PII
	} else if (type === 'result') {
		// For results - keep metadata but not full data payloads
		if (data.success !== undefined) sanitized.success = data.success
		if (data.error) sanitized.error = data.error
		if (data.data && Array.isArray(data.data)) {
			sanitized.dataCount = data.data.length
			sanitized.dataType = 'array'
		} else if (data.data) {
			sanitized.dataType = typeof data.data
			if (data.data.id) sanitized.resultId = data.data.id
		}
		if (data.message) sanitized.message = data.message
	}

	return sanitized
}

/**
 * BaseTool - Abstract base class for all AI agent tools
 * Tools are the fundamental actions the agent can perform (e.g., database queries, API calls)
 */
export class BaseTool {
	/**
	 * @param {Object} config - Tool configuration
	 * @param {string} config.name - Unique tool name
	 * @param {string} config.description - What the tool does
	 * @param {number} config.powerLevel - Access level (1=basic, 2=moderate, 3=advanced, 4=admin)
	 * @param {Object} config.schema - Expected parameters schema
	 */
	constructor({ name, description, powerLevel = 1, schema = {} }) {
		if (new.target === BaseTool) {
			throw new Error('BaseTool is abstract and cannot be instantiated directly')
		}

		if (!name || !description) {
			throw new Error('Tool name and description are required')
		}

		this.name = name
		this.description = description
		this.powerLevel = powerLevel
		this.schema = schema
		this.executionCount = 0
		this.lastExecuted = null
	}

	/**
	 * Validate parameters against schema
	 * @param {Object} params - Parameters to validate
	 * @returns {Object} - { valid: boolean, errors: string[] }
	 */
	validateParams(params) {
		const errors = []

		// Check required parameters
		Object.entries(this.schema).forEach(([key, config]) => {
			if (config.required && !(key in params)) {
				errors.push(`Missing required parameter: ${key}`)
			}

			// Type validation
			if (key in params && config.type) {
				const actualType = Array.isArray(params[key]) ? 'array' : typeof params[key]
				if (actualType !== config.type) {
					errors.push(`Parameter ${key} must be of type ${config.type}, got ${actualType}`)
				}
			}
		})

		return {
			valid: errors.length === 0,
			errors
		}
	}

	/**
	 * Execute the tool - Must be implemented by subclasses
	 * @param {Object} _params - Tool parameters
	 * @param {Object} _context - Execution context (user, tenant, etc.)
	 * @returns {Promise<Object>} - { success: boolean, data: any, error?: string }
	 */
	async execute(_params, _context) {
		throw new Error('execute() must be implemented by subclass')
	}

	/**
	 * Execute with validation and logging
	 * @param {Object} params - Tool parameters
	 * @param {Object} context - Execution context
	 * @returns {Promise<Object>}
	 */
	async run(params, context) {
		// Validate parameters
		const validation = this.validateParams(params)
		if (!validation.valid) {
			log(`Tool ${this.name} validation failed:`, validation.errors)
			return {
				success: false,
				error: `Validation failed: ${validation.errors.join(', ')}`
			}
		}

		// Track execution
		this.executionCount++
		this.lastExecuted = new Date()

		log(`Executing tool: ${this.name}`, {
			params: sanitizeForLogging(params, 'params'),
			context: sanitizeForLogging(context, 'context')
		})

		try {
			const result = await this.execute(params, context)
			log(`Tool ${this.name} executed successfully`, sanitizeForLogging(result, 'result'))
			return result
		} catch (error) {
			log(`Tool ${this.name} execution failed:`, error)
			return {
				success: false,
				error: error.message || 'Unknown error occurred'
			}
		}
	}

	/**
	 * Get tool metadata
	 * @returns {Object}
	 */
	getMetadata() {
		return {
			name: this.name,
			description: this.description,
			powerLevel: this.powerLevel,
			schema: this.schema,
			executionCount: this.executionCount,
			lastExecuted: this.lastExecuted
		}
	}
}
